import { generateImgSrc } from 'helpers/images';
import type { Image } from 'shared';
import type { AccessPath, AccessPathStep, Entrance } from './types';
import type { AccessMethod } from './types';

/** Access method types that require a custom name (user must enter, e.g. "White keycard") */
export const ACCESS_METHOD_TYPES_REQUIRING_NAME = ['keycard', 'key_fob', 'physical_key'];

/** Display labels for access path categories, keyed by path_type. */
export const PATH_TYPE_LABELS: Record<string, string> = {
  main: 'Main access',
  freight: 'Freight entrance'
};

export const getPathTypeLabel = (pathType: string): string => PATH_TYPE_LABELS[pathType] ?? pathType;

/** Vendor-facing label for an access method: its custom name if present, else the Title-Cased type. */
export const getAccessMethodLabel = (accessMethod: { name?: string | null; access_method_type: string }): string => {
  const name = accessMethod.name?.trim();
  return name && name.length > 0 ? name : getDefaultAccessMethodName(accessMethod.access_method_type);
};

const formatAccessMethodLabel = (type: string) =>
  type
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const isAccessMethodNameInvalid = (name: string, accessMethodTypes: string[]): boolean => {
  const normalized = name.trim().toLowerCase();
  if (normalized === '') return false;
  return accessMethodTypes.some(
    type => type.toLowerCase() === normalized || formatAccessMethodLabel(type).toLowerCase() === normalized
  );
};

export const getDefaultAccessMethodName = (accessMethodType: string): string =>
  formatAccessMethodLabel(accessMethodType);

export const getEntranceImageUrl = (entrance: { images?: Image[] } | null): string | null => {
  const key = entrance?.images?.[0]?.key;
  return key ? generateImgSrc({ srcKey: key }) : null;
};

export const getEntranceForPathStep = (step: AccessPathStep, entrances: Entrance[]): Entrance | null => {
  return entrances.find(e => e.id === step.entrance_id) ?? null;
};

/** Matches preview tabs: published path with at least one step, for main or freight, scoped to the building unit. */
export const accessPathQualifiesForOnboardingChecklist = (
  path: AccessPath,
  buildingUnitId: number | null | undefined
): boolean => {
  if (!path.is_published) return false;
  if ((path.access_path_steps?.length ?? 0) < 1) return false;
  if (buildingUnitId !== null && buildingUnitId !== undefined && path.building_unit_id !== buildingUnitId) {
    return false;
  }
  return path.path_type === 'main' || path.path_type === 'freight';
};

export const hasPublishedVendorAccessForBuildingUnit = (
  accessPaths: AccessPath[] | null | undefined,
  buildingUnitId: number | null | undefined
): boolean => {
  if (!accessPaths || accessPaths.length === 0) return false;
  if (buildingUnitId === null || buildingUnitId === undefined) return false;
  return accessPaths.some(p => accessPathQualifiesForOnboardingChecklist(p, buildingUnitId));
};

export const hasRealAccessMethodInPathSteps = (
  accessPathSteps: AccessPathStep[],
  accessMethods: AccessMethod[]
): boolean =>
  accessPathSteps.some(step =>
    (step.access_path_step_access_methods ?? []).some(apsam => {
      const am = accessMethods.find(a => a.id === apsam.access_method_id);
      return am?.access_method_type !== 'does_not_apply';
    })
  );

/**
 * Access method IDs already used for a given entrance on path steps.
 * When entranceId is undefined (e.g. adding a new entrance), returns empty set so all saved methods show.
 * When entranceId is set (e.g. editing), excludes only methods this entrance is already using.
 */
export const getUsedAccessMethodIdsOnPath = (accessPathSteps: AccessPathStep[], entranceId?: number): Set<number> => {
  const used = new Set<number>();
  if (entranceId == null) return used;
  for (const step of accessPathSteps ?? []) {
    if (step.entrance_id !== entranceId) continue;
    for (const apsam of step.access_path_step_access_methods ?? []) {
      used.add(apsam.access_method_id);
    }
  }
  return used;
};

/** Derived from path steps + access methods for display (entrance method count, etc.) */
export interface PathEntranceAccessMethod {
  entrance_id: number;
  access_method_id: number;
  access_method_type?: string;
  access_method_name?: string;
}

export const derivePathEntranceAccessMethods = (
  accessPathSteps: AccessPathStep[],
  accessMethods: AccessMethod[]
): PathEntranceAccessMethod[] => {
  const result: PathEntranceAccessMethod[] = [];
  for (const step of accessPathSteps ?? []) {
    for (const apsam of step.access_path_step_access_methods ?? []) {
      const am = accessMethods.find(a => a.id === apsam.access_method_id);
      result.push({
        entrance_id: step.entrance_id,
        access_method_id: apsam.access_method_id,
        access_method_type: am?.access_method_type,
        access_method_name: am?.name
      });
    }
  }
  return result;
};

/**
 * Next `position` when appending an access path step. Must be unique per path.
 * Using `steps.length + 1` is wrong after deletes (e.g. single step at position 2 → would reuse 2).
 */
export function getNextAccessPathStepPosition(steps: { position: number }[] | undefined | null): number {
  const list = steps ?? [];
  if (list.length === 0) return 1;
  return Math.max(...list.map(s => s.position)) + 1;
}
